import { getFreshToken } from '@/lib/firebase';
import { intlRequestNo } from '@staff-app/shared-types';

/**
 * Download a request's quote PDF.
 *
 * WHY NOT AN `<a href>`. The route is `GET /api/intl-requests/:id/quote.pdf` behind
 * StaffFirebaseGuard + AdminRoleGuard, so it needs an `Authorization: Bearer` header — which a
 * plain link cannot send. The link would arrive unauthenticated, 401, and the browser would show
 * the operator a blank tab rather than an error.
 *
 * WHY NOT A REDIRECT TO STRIPE'S OWN PDF URL. That hands a customer-facing pricing document to
 * whoever holds the link, outside our auth and our audit entirely — see the controller.
 *
 * So: fetch with the token, then hand the bytes to the browser as a blob. The object URL is
 * revoked immediately after the click; Chrome has already read it by then, and leaving it alive
 * pins the whole PDF in memory for the life of the tab.
 */
const API_BASE = (import.meta.env.VITE_GRAPHQL_URL as string).replace(/\/graphql\/?$/, '');

/** Pulled out of the response, falling back to a name derived from the request. */
function filenameFrom(res: Response, requestId: number): string {
  const cd = res.headers.get('content-disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  return m?.[1] || `optisigns-quote-${intlRequestNo(requestId)}.pdf`;
}

export async function downloadIntlQuotePdf(requestId: number): Promise<void> {
  const { token } = await getFreshToken();

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/intl-requests/${requestId}/quote.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // fetch rejects both when nothing arrives AND when a response arrived without CORS headers
    // (an nginx error page carries none). Saying that beats leaking "Failed to fetch" into a
    // toast, which is the least diagnosable string the platform produces.
    throw new Error(
      'Could not reach the staff API — the request was rejected before it arrived (network or ' +
        'API gateway).',
    );
  }

  if (!res.ok) {
    // THE SERVER'S OWN MESSAGE, which is worth reading: "was never finalized in Stripe … supersede
    // it to raise a fresh one" names the fix, and replacing it with a status code would not.
    let msg = `Could not download the quote (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      const m = Array.isArray(body.message) ? body.message.join(', ') : body.message;
      if (m) msg = m;
    } catch {
      // A non-JSON error page (a gateway's own HTML). The status line is all there is.
    }
    throw new Error(msg);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameFrom(res, requestId);
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
